import { computed, watch } from "vue";

/**
 * Keeps the activator field's text on the selection.
 *
 * The field pins user-typed text against attribute changes (the dirty value flag), so the
 * selection's title is written to its live `value` property, the channel that always adopts.
 * `activatorText` is the attribute seed. The field shows the selection whenever the picker is
 * closed, and is empty while the activator is icon-only.
 *
 * Server-side filtering replaces `items` wholesale, so a selected entry can leave the list while it
 * is still the selection. The title last resolved for each entry is remembered, keyed the way
 * `findItem` compares, so a value that changes type still matches.
 *
 * @param {object} props  Owning component's reactive props; reads `prompt`, `prependSelectedWith`.
 * @param {{ model: import("vue").Ref<any[]>, items: import("vue").Ref<any[]>,
 *   multiple: import("vue").Ref<boolean>, iconOnly: import("vue").Ref<boolean>,
 *   hasSelection: import("vue").Ref<boolean>, menuOpen: import("vue").Ref<boolean>,
 *   field: import("vue").Ref<HTMLElement | null>, findItem: (value: any) => any,
 *   getItemTitle: (item: any) => any, selectedTagsEnabled: import("vue").Ref<boolean> }} options  `field` is the activator's `sp-text-field`.
 * @returns {{ activatorText: import("vue").ComputedRef<string|undefined>, restoreActivatorText: () => void }}
 */
export function useActivatorText(
  props,
  { model, items, multiple, iconOnly, hasSelection, menuOpen, field, findItem, getItemTitle, selectedTagsEnabled },
) {
  const textValue = computed(() => {
    if (selectedTagsEnabled.value) {
      return "";
    }
    if (multiple.value) {
      return model.value?.length ? `${model.value.length} selected` : "";
    }

    const item = findItem(model.value[0]);
    const title = getItemTitle(item);

    if (item?.placeholder && !String(title).trim()) {
      return undefined;
    }

    if (title) {
      const isPrompt = props.prompt && items.value[0] === item;
      const prepend = props.prependSelectedWith && !isPrompt ? props.prependSelectedWith : null;

      return [prepend, title].filter(Boolean).join(" ");
    }

    return undefined;
  });

  const activatorText = computed(() => (iconOnly.value ? "" : textValue.value));

  const resolvedTitles = new Map();

  watch([() => model.value[0], textValue], cacheResolvedTitle, {
    immediate: true,
  });

  watch([() => model.value[0], textValue], syncActivatorText);

  watch(iconOnly, restoreActivatorText);

  watch(menuOpen, restoreClosedActivator);

  function cacheResolvedTitle(selectionState) {
    const [entry, value] = selectionState;

    if (value !== undefined) {
      resolvedTitles.set(String(entry), value);
    }
  }

  function titleForCurrentSelection() {
    return resolvedTitles.get(String(model.value[0]));
  }

  function syncActivatorText(selectionState, previousState) {
    const [previousEntry] = previousState;

    const [entry, value] = selectionState;

    if (!field.value) {
      return;
    }

    if (selectedTagsEnabled.value && menuOpen.value) {
      return;
    }

    if (iconOnly.value) {
      field.value.value = "";

      return;
    }

    // An unchanged selection leaves the field alone while its title is unresolved: that is item-list
    // churn or the user typing over it, and the write lands once the title resolves.
    if (entry === previousEntry) {
      if (value === undefined && hasSelection.value) {
        return;
      }

      field.value.value = value ?? "";

      return;
    }

    // A changed selection always writes, so the field cannot show a stale title. The remembered title
    // covers an entry the current `items` cannot resolve.
    field.value.value = value ?? titleForCurrentSelection() ?? "";
  }

  // Typing writes the inner input directly, which no model watcher sees, so text the user typed
  // without picking anything is replaced once the picker closes.
  function restoreClosedActivator(open) {
    if (!open) {
      restoreActivatorText();
    }
  }

  function restoreActivatorText() {
    if (!field.value) {
      return;
    }

    if (iconOnly.value) {
      field.value.value = "";

      return;
    }

    const title = textValue.value ?? titleForCurrentSelection();

    // An unresolved title on a live selection is item-list churn, not an empty field.
    if (title === undefined && hasSelection.value) {
      return;
    }

    field.value.value = title ?? "";
  }

  return {
    activatorText,
    restoreActivatorText,
  };
}
